import { Localized, useLocalization } from "@fluent/react"
import { Alert, Box, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material"
import { useState } from "react"
import { AppId } from "../../../../app/appTypes"
import { useAppDispatch, useAppSelector } from "../../../../app/hooks"
import { showError, showSuccess } from "../../../notifications/notificationsSlice"
import { SaveButton } from "../../../../app/components/SaveButton"
import { CancelButton } from "../../../../app/components/CancelButton"
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import { addDocumentSectionQuestion, CreateUpdateDocumentQuestionPayload, DocumentQuestion, updateDocumentSectionQuestion } from "../documentQuestionsApi"
import { RequiredError } from "../../../../app/components/RequiredError"
import CommentIcon from '@mui/icons-material/Comment'
import { SectionComments } from "../../SectionComments"
import { isDocEditable } from "../../documentSlice"

export interface AddEditDocumentQuestionProps {
    documentId: AppId
    sectionId: string
    questionRow: DocumentQuestion | null
    mode: "create" | "edit" | "comment"
    onSuccessfulSave: (changeLog: DocumentQuestion) => void
    onCancelClick: () => void,
}

export const AddEditDocumentQuestion = ({ documentId, sectionId, mode, questionRow, onCancelClick, onSuccessfulSave }: AddEditDocumentQuestionProps) => {
    const dispatch = useAppDispatch()
    const { l10n } = useLocalization()

    const [saving, setSaving] = useState(false)
    const [serverError, setServerError] = useState("")
    const [questionText, setQuestionText] = useState<string>(questionRow?.text || "");
    const [validationErrors, setValidationErrors] = useState(false)
    const isCommentable =  mode === 'comment'
    const isEditable = useAppSelector(isDocEditable)

    const questionTextError = validationErrors && questionText === ""

    const handleOkClick = async () => {
        setValidationErrors(true)
        if (!documentId || !sectionId || !questionText) return;

        if (mode === "edit" && !questionRow?.id) {
            return;
        }

        setSaving(true);
        try {
            const payload = { text: questionText, type: 'YesNo', globalAnswer: '' } as CreateUpdateDocumentQuestionPayload;
            let result;

            if (mode === "create") {
                result = await dispatch(addDocumentSectionQuestion({ documentId, sectionId, payload }));

                if (addDocumentSectionQuestion.fulfilled.match(result)) {
                    const newQuestion = result.payload as DocumentQuestion;
                    dispatch(showSuccess("saved"));
                    onSuccessfulSave(newQuestion);
                    return;
                }
            }

            if (mode === "edit") {
                result = await dispatch(updateDocumentSectionQuestion({ documentId, sectionId, questionId: questionRow!.id, payload }));

                if (updateDocumentSectionQuestion.fulfilled.match(result)) {
                    const updatedQuestion = result.payload as DocumentQuestion;
                    dispatch(showSuccess("saved"));
                    onSuccessfulSave(updatedQuestion);
                    return;
                }
            }

            let errorMsg = "error";
            if (result?.payload?.kind === "http" && result.payload.problem) {
                errorMsg = result.payload.problem.title;
            }
            setServerError(errorMsg);

        } catch (error) {
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };

    const content = <>
        <TextField
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            error={questionTextError}
            helperText={questionTextError && <RequiredError />}
            disabled={!isEditable}
            multiline
            minRows={3}
            required
            margin="normal"
            variant="outlined"
            label={l10n.getString("document-question-text", null, "_text")}
        />

        {serverError && (
            <Alert severity="error" sx={{ mt: 2 }}>
                {serverError}
            </Alert>
        )}
    </>

    let title = <></>
    switch (mode) {
        case 'create':
            title = <>
                <AddCircleIcon fontSize="small" sx={{ mr: 1 }} color="success" />
                <Localized id="document-question-add">
                    <span>_add</span>
                </Localized>

            </>
            break;
        case 'edit':
            title = <>
                <EditIcon fontSize="small" sx={{ mr: 1 }} color="info" />
                <Localized id="document-question-edit">
                    <span>_edit</span>
                </Localized>
            </>
            break;
        case 'comment':
            title = <>
                <CommentIcon fontSize="small" sx={{ mr: 1 }} color="success" />
                <Localized id="coments">
                    <span>_Komentarze</span>
                </Localized>

            </>
            break;
    }


    return <>
        <Dialog key="addEditDocumentChangeLog" open={true} maxWidth="lg" fullWidth keepMounted={false}>
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    bgcolor: "#f9f9f9",
                    color: "text.primary",
                    fontWeight: "bold",
                }}
            >
                {title}
            </DialogTitle>
            <DialogContent dividers>
                {isCommentable
                    ? <SectionComments
                        sectionId={sectionId}
                        contextType='question'
                        contextGuid={questionRow?.guid ?? ''}
                        component={content}
                    />
                    : <Box sx={{
                        display: 'flex',
                        [`& > div`]: {
                            flexGrow: 1,
                        },
                    }}>{content}</Box>}
            </DialogContent>
            <DialogActions sx={{ p: 2, justifyContent: "flex-end" }}>
                <SaveButton disabled={!isEditable} loading={saving} onClick={handleOkClick}></SaveButton>
                <CancelButton loading={saving} onClick={onCancelClick}></CancelButton>
            </DialogActions>
        </Dialog>
    </>
}
